import { useEffect, useState } from "react";
import {
  SolutionHistoryHeader,
  SolutionHistoryItemWrapper,
  SolutionHistoryListWrapper,
  SolutionHistoryTable,
  SolutionHistoryThead,
  SolutionHistoryWrapper,
} from "./style";
import { PaginationWrapper } from "../../../../component/CommonTable";
import PopupPagination from "../../../../component/Pagination/popupPagination";
import { formatDate } from "../../../../utils/date";
import { Title } from "../../../../styles/page";
import {
  getSettlementPubPointLogs,
  SettlementResponse,
  getSettlementRabbitLogs,
  getSettlementLegacySeedLogs,
  getSettlementCarrotLogs,
  SettlementLogResponse,
} from "../../../../api/settlement";
import DropBox from "../../../../component/DropBox";
import { MemberSearchBox } from "../../../member/style";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../../recoil/pub.atom";
import { formatLogType } from "../../utils/settlement.util";

interface SolutionHistoryProps {
  onClose: () => void;
  type: string;
  data: SettlementResponse | undefined;
  dailyDate: string;
}
const SolutionHistory = ({
  onClose,
  type,
  data,
  dailyDate,
}: SolutionHistoryProps) => {
  const pub = useRecoilValue(pubState);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list, setList] = useState<SettlementLogResponse[]>([]);
  const [allList, setAllList] = useState<SettlementLogResponse[]>([]);
  const [dateFilter, setDateFilter] = useState<string>("DAY");
  const [searchMember, setSearchMember] = useState<string>("");

  const onPageChange = async (nextPage: number) => {
    setCurrentPage(nextPage);
  };

  async function getSeedLogs() {
    if (!pub) return [];
    const res =
      type === "LEGACY_SEED"
        ? await getSettlementLegacySeedLogs(pub.id, dailyDate, dateFilter)
        : await getSettlementRabbitLogs(pub.id, dailyDate, dateFilter);
    return res ?? [];
  }

  async function getCarrotLogs() {
    if (!pub) return [];
    const res = await getSettlementCarrotLogs(pub.id, dailyDate, dateFilter);
    return res ?? [];
  }

  async function getPubPointLogs() {
    if (!pub) return [];
    const res = await getSettlementPubPointLogs(pub.id, dailyDate, dateFilter);
    return res;
  }

  async function setLogList() {
    if (!data) return;
    setSearchMember("");
    const list =
      type === "POINT"
        ? await getPubPointLogs()
        : type === "CARROT"
          ? await getCarrotLogs()
          : await getSeedLogs();
    setCurrentPage(1);
    setAllList(list);
    if (list && list.length > 0) {
      setList(list.slice((currentPage - 1) * 10, currentPage * 10));
      setTotalPage(Math.max(1, Math.ceil(list.length / 10)));
    } else {
      setList([]);
      setTotalPage(0);
    }
  }

  useEffect(() => {
    setLogList();
  }, [data, type, dateFilter]);

  const Header = [
    "날짜",
    "매장명",
    "닉네임",
    "구분",
    "매장 기존 보유 수량",
    "사용 수량",
    "매장 최종 보유 수량",
    "비고",
  ];

  const dateFilterList = [
    {
      name: "오늘",
      value: "DAY",
    },
    {
      name: "이번주",
      value: "WEEK",
    },
    {
      name: "이번달",
      value: "MONTH",
    },
  ];

  const onSearchClick = () => {
    const filteredList = allList.filter((item) =>
      item.nickname.toLowerCase().includes(searchMember.toLowerCase()),
    );
    setList(filteredList.slice((currentPage - 1) * 10, currentPage * 10));
    setTotalPage(Math.max(1, Math.ceil(filteredList.length / 10)));
  };

  useEffect(() => {
    onSearchClick();
  }, [searchMember, currentPage, allList]);

  return (
    <SolutionHistoryWrapper>
      <SolutionHistoryHeader>
        <Title>
          {type === "POINT"
            ? "매장 이용권"
            : type === "LEGACY_SEED"
              ? "(구)크레딧"
              : type === "CARROT"
                ? "캐럿"
                : "크레딧"}{" "}
          내역
        </Title>
        <div className="left">
          <MemberSearchBox>
            <input
              type="text"
              placeholder="닉네임을 검색하세요. "
              value={searchMember}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  onSearchClick();
                }
              }}
              onChange={(e) => {
                onPageChange(1);
                setSearchMember(e.target.value);
              }}
            />
            <div className="searchIcon" onClick={onSearchClick}>
              <img
                src="/image-web/timer-config/searchIcon.svg"
                alt="검색 아이콘"
              />
            </div>
          </MemberSearchBox>
          <DropBox
            list={dateFilterList}
            value={dateFilter}
            setValue={setDateFilter}
          />
          <img
            src="/image-web/solution/close.svg"
            alt="close"
            onClick={() => onClose()}
          />
        </div>
      </SolutionHistoryHeader>
      <SolutionHistoryTable>
        <SolutionHistoryThead>
          {Header.map((item, index) => (
            <div className={`item i${index}`}>{item}</div>
          ))}
        </SolutionHistoryThead>
        <SolutionHistoryListWrapper>
          {list.length > 0 ? (
            list.map((item, index) => (
              <SolutionHistoryItemWrapper key={index}>
                <div className="item i0">{formatDate(item.date, true)}</div>
                <div className="item i1">
                  <span>{item.pubName}</span>
                </div>
                <div className="item i2">
                  <span>{item.nickname}</span>
                </div>
                <div className="item i3">{formatLogType(item.type)}</div>
                <div className="item i4">
                  {Number(item.previousAmount).toLocaleString()}
                </div>
                <div className="item i5">
                  {Number(item.amount).toLocaleString()}
                </div>
                <div className="item i6">
                  {Number(item.finalAmount).toLocaleString()}
                </div>
                <div className="item i7">
                  {type === "POINT" ? item.memo : "-"}
                </div>
              </SolutionHistoryItemWrapper>
            ))
          ) : (
            <div className="no-data">내역이 없습니다. </div>
          )}
        </SolutionHistoryListWrapper>
      </SolutionHistoryTable>
      {totalPage > 1 && (
        <PaginationWrapper>
          <PopupPagination
            currentPage={currentPage}
            totalPages={totalPage}
            onPageChange={onPageChange}
          />
        </PaginationWrapper>
      )}
    </SolutionHistoryWrapper>
  );
};

export default SolutionHistory;
